import { useCallback, useEffect, useState } from 'react';
import type { DonHang, DonHangResponse, DonHangInput } from '@/types/schemas/don-hang.type';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

/**
 * Return type for the useDonHang hook
 */
interface UseDonHangReturn {
  donHangs: DonHang[];
  isLoading: boolean;
  isLoadingDetail: boolean;
  addDonHang: (newDonHang: DonHangInput) => Promise<DonHang>;
  updateDonHang: (uuid: string, updatedDonHang: DonHangInput) => Promise<DonHang>;
  deleteDonHang: (uuid: string) => Promise<void>;
  refreshDonHangs: () => Promise<void>;
  getDonHangByCustomer: (customerId: string) => Promise<DonHang[]>;
  getDonHangByStatus: (status: string) => Promise<DonHang[]>;
  getDonHangBySalesEmployee: (employeeId: string) => Promise<DonHang[]>;
  getDonHangByDateRange: (startDate: string, endDate: string) => Promise<DonHang[]>;
  getDonHangDetail: (uuid: string) => Promise<[]>;
  searchDonHang: (searchParams: any) => Promise<DonHang[]>;
  filterDonHangs: (filters: Record<string, any>) => Promise<DonHang[]>;
  approveDonHang: (uuid: string) => Promise<DonHang>;
  cancelDonHang: (uuid: string) => Promise<DonHang>;
  bulkCancelDonHang: (
    uuids: string[]
  ) => Promise<{ success_count: number; failed_count: number; failed_uuids: string[] }>;
}

/**
 * Hook for managing DonHang (Sales Order) data
 *
 * This hook provides functions to fetch, create, update, and delete sales orders.
 * It includes various filtering and search capabilities for sales order management.
 *
 * @example
 * ```tsx
 * const {
 *   donHangs,
 *   isLoading,
 *   addDonHang,
 *   updateDonHang,
 *   getDonHangByCustomer,
 *   searchDonHang
 * } = useDonHang();
 *
 * // Create a new sales order
 * const handleCreate = async (data: DonHangInput) => {
 *   try {
 *     const newOrder = await addDonHang(data);
 *     console.log('Created:', newOrder);
 *   } catch (error) {
 *     console.error('Failed to create:', error);
 *   }
 * };
 *
 * // Search sales orders
 * const handleSearch = async (params: any) => {
 *   const results = await searchDonHang(params);
 *   console.log('Search results:', results);
 * };
 * ```
 */
export const useDonHang = (initialDonHangs: DonHang[] = []): UseDonHangReturn => {
  const [donHangs, setDonHangs] = useState<DonHang[]>(initialDonHangs);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);

  const { entity } = useAuth();

  /**
   * Fetch all sales orders
   */
  const fetchDonHangs = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<DonHangResponse>(`/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG}/`);
      setDonHangs(response.data.results);
    } catch (error) {
      console.error('Error fetching sales orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Add a new sales order
   */
  const addDonHang = async (newDonHang: DonHangInput): Promise<DonHang> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.post<DonHang>(`/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG}/`, newDonHang);

      const addedDonHang = response.data;
      setDonHangs(prev => [...prev, addedDonHang]);
      return addedDonHang;
    } catch (error) {
      console.error('Error adding sales order:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Update an existing sales order
   */
  const updateDonHang = async (uuid: string, updatedDonHang: DonHangInput): Promise<DonHang> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.put<DonHang>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG}/${uuid}/`,
        updatedDonHang
      );

      const updated = response.data;
      setDonHangs(prev => prev.map(item => (item.uuid === uuid ? updated : item)));
      return updated;
    } catch (error) {
      console.error('Error updating sales order:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete a sales order
   */
  const deleteDonHang = async (uuid: string): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG}/${uuid}/`);
      setDonHangs(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting sales order:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get sales orders by customer
   */
  const getDonHangByCustomer = async (customerId: string): Promise<DonHang[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<DonHangResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG}/?ma_kh=${customerId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching sales orders by customer:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get sales orders by status
   */
  const getDonHangByStatus = async (status: string): Promise<DonHang[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<DonHangResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG}/?status=${status}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching sales orders by status:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get sales orders by sales employee
   */
  const getDonHangBySalesEmployee = async (employeeId: string): Promise<DonHang[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<DonHangResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG}/?ma_nvbh=${employeeId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching sales orders by employee:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get sales orders by date range
   */
  const getDonHangByDateRange = async (startDate: string, endDate: string): Promise<DonHang[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<DonHangResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG}/?ngay_ct_from=${startDate}&ngay_ct_to=${endDate}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching sales orders by date range:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get sales order detail
   */
  const getDonHangDetail = async (uuid: string): Promise<[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoadingDetail(true);
    try {
      const response = await api.get<[]>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG}/don-hang/${uuid}/chi-tiet/`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching sales order detail:', error);
      throw error;
    } finally {
      setIsLoadingDetail(false);
    }
  };

  /**
   * Search sales orders with custom parameters
   */
  const searchDonHang = async (searchParams: any): Promise<DonHang[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const queryString = new URLSearchParams(searchParams).toString();
      const response = await api.get<DonHangResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG}/?${queryString}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error searching sales orders:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Filter sales orders using POST /filter/ endpoint
   */
  const filterDonHangs = useCallback(
    async (filters: Record<string, any>): Promise<DonHang[]> => {
      if (!entity?.slug) return [];

      try {
        const response = await api.post(`/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG}/filter/`, filters);

        if (Array.isArray(response.data)) {
          return response.data;
        }

        if (response.data?.results) {
          return response.data.results;
        }

        return [];
      } catch (error) {
        console.error('Error filtering sales orders via filter API:', error);
        return [];
      }
    },
    [entity?.slug]
  );

  /**
   * Approve a sales order
   */
  const approveDonHang = async (uuid: string): Promise<DonHang> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.put<DonHang>(`/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG}/${uuid}/approve/`);

      const approved = response.data;
      setDonHangs(prev => prev.map(item => (item.uuid === uuid ? approved : item)));
      return approved;
    } catch (error) {
      console.error('Error approving sales order:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Cancel a sales order
   * After cancelling, refresh data and save automatically
   */
  const cancelDonHang = async (uuid: string): Promise<DonHang> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.put<DonHang>(`/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG}/${uuid}/cancel/`);
      const cancelled = response.data;

      await refreshDonHangs();

      return cancelled;
    } catch (error) {
      console.error('Error cancelling sales order:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Bulk cancel multiple sales orders
   * After cancelling, refresh data and save automatically
   */
  const bulkCancelDonHang = async (
    uuids: string[]
  ): Promise<{ success_count: number; failed_count: number; failed_uuids: string[] }> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.put<{ success_count: number; failed_count: number; failed_uuids: string[] }>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.DON_HANG}/bulk-cancel/`,
        { uuids }
      );
      const result = response.data;

      await refreshDonHangs();

      return result;
    } catch (error) {
      console.error('Error bulk cancelling sales orders:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refresh sales orders data
   */
  const refreshDonHangs = async (): Promise<void> => {
    await fetchDonHangs();
  };

  useEffect(() => {
    fetchDonHangs();
  }, [entity?.slug]);

  return {
    donHangs,
    isLoading,
    isLoadingDetail,
    addDonHang,
    updateDonHang,
    deleteDonHang,
    refreshDonHangs,
    getDonHangByCustomer,
    getDonHangByStatus,
    getDonHangBySalesEmployee,
    getDonHangByDateRange,
    getDonHangDetail,
    searchDonHang,
    filterDonHangs,
    approveDonHang,
    cancelDonHang,
    bulkCancelDonHang
  };
};

export const fetchAllDonHangs = async (entitySlug: string, pageSize = 200): Promise<DonHang[]> => {
  let page = 1;
  let all: DonHang[] = [];
  let totalCount = 0;

  while (true) {
    const response = await api.get<DonHangResponse>(`/entities/${entitySlug}/erp/${QUERY_KEYS.DON_HANG}/`, {
      params: {
        page,
        page_size: pageSize
      }
    });

    const pageResults = response.data.results || [];
    totalCount = response.data.count ?? totalCount;
    all = all.concat(pageResults);

    if (!pageResults.length) {
      break;
    }

    const hasNext = (response.data as any).next;

    if (!hasNext && pageResults.length < pageSize) {
      break;
    }

    if (totalCount && all.length >= totalCount) {
      break;
    }

    page += 1;
  }

  return all;
};
